/**
 * Nextmine: samler-profilen - den lille bio på en samlers egen side.
 *
 * Nogle få, konkrete spørgsmål frem for et tomt "fortæl om dig selv"-felt.
 * Et tomt felt får folk til at skrive ingenting; et spørgsmål som "hvor jagter
 * du?" får dem til at skrive noget, andre kan genkende sig selv i. Alle
 * felter er valgfri - man udfylder dem, man har lyst til.
 *
 * Værdierne gemmes i brugerens publicData (offentlige, ligesom bio og land).
 * Felterne er defineret her i koden og ikke i Console, fordi de er en del af
 * samlings-oplevelsen og skal vises i vores eget design.
 */

/**
 * Svarene er små historier, ikke etiketter - så der skal være plads til en
 * sætning eller to. Formularen viser hvor mange tegn der er tilbage, så
 * grænsen aldrig rammes som en overraskelse midt i en sætning.
 */
export const FIELD_MAX_LENGTH = 300;

export const COLLECTOR_FIELDS = [
  { key: 'collectingSince', maxLength: FIELD_MAX_LENGTH },
  { key: 'favouriteEra', maxLength: FIELD_MAX_LENGTH },
  { key: 'collects', maxLength: FIELD_MAX_LENGTH },
  { key: 'favouritePiece', maxLength: FIELD_MAX_LENGTH },
  { key: 'neverTakeOff', maxLength: FIELD_MAX_LENGTH },
  { key: 'inspiration', maxLength: FIELD_MAX_LENGTH },
  { key: 'grail', maxLength: FIELD_MAX_LENGTH },
];

export const COLLECTOR_FIELD_KEYS = COLLECTOR_FIELDS.map(f => f.key);

/**
 * Spørgsmål der IKKE vises nogen steder. Svaret gemmes i brugerens privateData,
 * som kun brugeren selv og vi som marketplace kan se (i Console) - aldrig andre
 * medlemmer. Feltet er mærket tydeligt i formularen: vi spørger åbent om noget
 * privat i stedet for at udlede det bag ryggen på folk.
 */
export const PRIVATE_COLLECTOR_FIELDS = [{ key: 'buysFrom', maxLength: FIELD_MAX_LENGTH }];

// NB: hvor man koeber, spoerger vi KUN om skjult (Lines beslutning 2026-08-04).
// Det offentlige "Where I hunt" er droppet - to naesten ens spoergsmaal fik
// folk til at svare det samme to gange, og det ene skulle alligevel vaere privat.

export const PRIVATE_COLLECTOR_FIELD_KEYS = PRIVATE_COLLECTOR_FIELDS.map(f => f.key);

/** Formularens startværdier, hentet fra brugerens publicData. */
export const collectorInitialValues = (publicData = {}) =>
  COLLECTOR_FIELD_KEYS.reduce((acc, key) => ({ ...acc, [key]: publicData?.[key] || '' }), {});

/**
 * Plukker felterne ud af formularens værdier til publicData.
 * Tomme felter gemmes som null, så et felt man fortryder faktisk forsvinder
 * igen i stedet for at blive hængende som en tom linje på profilen.
 */
export const collectorPublicData = (values = {}) =>
  COLLECTOR_FIELD_KEYS.reduce((acc, key) => {
    const value = typeof values[key] === 'string' ? values[key].trim() : '';
    return { ...acc, [key]: value || null };
  }, {});

/** De udfyldte felter, i den rækkefølge de skal vises. */
export const filledCollectorFields = (publicData = {}) =>
  COLLECTOR_FIELDS.filter(f => {
    const value = publicData?.[f.key];
    return typeof value === 'string' && value.trim().length > 0;
  }).map(f => ({ ...f, value: publicData[f.key].trim() }));

export const hasCollectorProfile = (publicData = {}) =>
  filledCollectorFields(publicData).length > 0;

/** Startværdier for de skjulte spørgsmål, hentet fra brugerens privateData. */
export const collectorPrivateInitialValues = (privateData = {}) =>
  PRIVATE_COLLECTOR_FIELD_KEYS.reduce(
    (acc, key) => ({ ...acc, [key]: privateData?.[key] || '' }),
    {}
  );

/** De skjulte svar, klar til privateData. Tomme felter ryddes, ligesom de offentlige. */
export const collectorPrivateData = (values = {}) =>
  PRIVATE_COLLECTOR_FIELD_KEYS.reduce((acc, key) => {
    const value = typeof values[key] === 'string' ? values[key].trim() : '';
    return { ...acc, [key]: value || null };
  }, {});
